import { useTranslation } from "react-i18next";
import { Card, CardContent, Skeleton } from "@tellescope/ui";
import type { WeightMacroTrackerBlock as WeightMacroTrackerBlockType } from "../../../../shared/schema";
import { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { MetricDetailView } from "./MetricDetailView";
import { LogEntryView } from "./LogEntryView";
import { TrackerDashboard } from "./TrackerDashboard";
import { TrackerHistory } from "./TrackerHistory";
import { Breadcrumbs } from "./Breadcrumbs";
import { useBreadcrumbs } from "./useBreadcrumbs";

interface WeightMacroTrackerBlockProps {
  block: WeightMacroTrackerBlockType;
}

const WeightMacroTrackerSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-24 w-full rounded-lg" />
    <Skeleton className="h-14 w-full rounded-lg" />
    <Skeleton className="h-14 w-full rounded-lg" />
  </div>
);

const ActiveView = ({ state }: { state: ReturnType<typeof useWeightMacroTrackerBlock> }) => {
  switch (state.view.name) {
    case "dashboard":
      return <TrackerDashboard state={state} />;
    case "history":
      return <TrackerHistory state={state} />;
    case "metric-detail":
      return <MetricDetailView state={state} metricId={state.view.metricId} />;
    case "log-entry":
      return <LogEntryView state={state} editEntryId={state.view.editEntryId} />;
    default: {
      const _exhaustive: never = state.view;
      return _exhaustive;
    }
  }
};

export const WeightMacroTrackerBlock = ({ block }: WeightMacroTrackerBlockProps) => {
  const { t } = useTranslation("portal");
  const state = useWeightMacroTrackerBlock();
  const breadcrumbs = useBreadcrumbs(state);

  if (state.isLoading) {
    return <WeightMacroTrackerSkeleton />;
  }

  if (state.error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">
          {block.props.emptyMessage ?? t("Failed to load nutrition data")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Breadcrumbs items={breadcrumbs} />
      <ActiveView state={state} />
    </div>
  );
};
